import type { ReactNode } from "react";
import { shouldInlineSecondaryRecordAudit, useSidebarLayout, useViewport } from "../../contexts";
import "./RecordAuditMetaBar.css";

export type RecordAuditMetaBarProps = {
    formatDate: (iso: string | null | undefined) => string;
    formatByLine: (name: string | null | undefined) => string;
    createdAt?: string | null;
    createdByDisplayName?: string | null;
    updatedAt?: string | null;
    lastUpdatedByDisplayName?: string | null;
    isDeleted: boolean;
    isEnabled: boolean;
    deletedAuditAtUtc?: string | null;
    deletedByDisplayName?: string | null;
    disabledAuditAtUtc?: string | null;
    disabledByDisplayName?: string | null;
};

type AuditBlockProps = {
    label: string;
    labelClassName: string;
    atUtc: string | null | undefined;
    byDisplayName: string | null | undefined;
    formatDate: (iso: string | null | undefined) => string;
    formatByLine: (name: string | null | undefined) => string;
};

function AuditBlock({
    label,
    labelClassName,
    atUtc,
    byDisplayName,
    formatDate,
    formatByLine,
}: AuditBlockProps) {
    return (
        <div className="d-flex text-start align-items-start" style={{ minWidth: "11rem" }}>
            <span className={`${labelClassName} text-nowrap me-2`}>{label}</span>
            <div>
                <div>{formatDate(atUtc)}</div>
                <div className="text-muted small">by {formatByLine(byDisplayName)}</div>
            </div>
        </div>
    );
}

function SecondaryAuditBlocks({
    props,
    fallbackUpdatedAt,
}: {
    props: RecordAuditMetaBarProps;
    fallbackUpdatedAt: string | null | undefined;
}): ReactNode {
    const { formatDate, formatByLine, isDeleted, isEnabled } = props;

    if (isDeleted) {
        return (
            <AuditBlock
                label="Deleted:"
                labelClassName="text-danger"
                atUtc={props.deletedAuditAtUtc ?? fallbackUpdatedAt}
                byDisplayName={props.deletedByDisplayName}
                formatDate={formatDate}
                formatByLine={formatByLine}
            />
        );
    }

    if (!isEnabled) {
        return (
            <AuditBlock
                label="Disabled:"
                labelClassName="text-warning"
                atUtc={props.disabledAuditAtUtc ?? fallbackUpdatedAt}
                byDisplayName={props.disabledByDisplayName}
                formatDate={formatDate}
                formatByLine={formatByLine}
            />
        );
    }

    return null;
}

export function RecordAuditMetaBar(props: RecordAuditMetaBarProps) {
    const { width } = useViewport();
    const { isSidebarCollapsed } = useSidebarLayout();
    const inlineSecondaryAudit = shouldInlineSecondaryRecordAudit(width, isSidebarCollapsed);

    const {
        formatDate,
        formatByLine,
        createdAt,
        createdByDisplayName,
        updatedAt,
        lastUpdatedByDisplayName,
    } = props;

    const secondaryAudit = (
        <SecondaryAuditBlocks props={props} fallbackUpdatedAt={updatedAt} />
    );

    return (
        <div className="d-flex flex-wrap align-items-start gap-4 mb-3">
            <div className="d-flex flex-wrap align-items-start record-audit-meta-bar__primary-group">
                {createdAt && (
                    <AuditBlock
                        label="Created:"
                        labelClassName="text-muted"
                        atUtc={createdAt}
                        byDisplayName={createdByDisplayName}
                        formatDate={formatDate}
                        formatByLine={formatByLine}
                    />
                )}
                {updatedAt && (
                    <AuditBlock
                        label="Last Updated:"
                        labelClassName="text-muted"
                        atUtc={updatedAt}
                        byDisplayName={lastUpdatedByDisplayName}
                        formatDate={formatDate}
                        formatByLine={formatByLine}
                    />
                )}
                {inlineSecondaryAudit && secondaryAudit}
            </div>
            {!inlineSecondaryAudit && secondaryAudit && (
                <div className="ms-auto d-flex flex-wrap gap-4 align-items-start">{secondaryAudit}</div>
            )}
        </div>
    );
}
